import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Sidebar from '../../../components/Sidebar';
import MeditationNav from '../MeditationNav';
import {
  HiMenu,
  HiSearch,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
  HiChevronDown,
  HiX,
  HiLockClosed,
  HiInformationCircle,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../../components/toaster';
import { uploadImage, uploadFile } from '../../../services/assetService';
import {
  listMeditationSessions,
  createMeditationSession,
  getMeditationSession,
  updateMeditationSession,
  deleteMeditationSession,
  listMeditationMainCategoriesWithSubcategories,
  listMeditationProgramCategories,
} from '../../../services/meditationService';
import {
  SESSION_TYPE_HELPER_TEXT,
  SESSION_TYPE_OPTIONS,
  applySessionTypeToCreatePayload,
  applySessionTypeToUpdatePayload,
  formatSessionTypeDisplay,
  sessionTypeToFormValue,
  validateSessionTypeFormValue,
} from './sessionTypeUtils';
import {
  applyAudioDurationToPayload,
  durationSecondsFromMedia,
  readAudioDurationSeconds,
  readAudioFileDurationSeconds,
} from './audioDurationUtils';

const PAGE_SIZE = 20;

const HTTP_URL_RE = /^https?:\/\/.+/i;

const normalizeImageUrl = (value) => String(value || '').trim();

/** Validate optional image URL: empty OK; non-empty must be http/https. */
const getImageFieldError = (value) => {
  const trimmed = normalizeImageUrl(value);
  if (!trimmed) return '';
  if (!HTTP_URL_RE.test(trimmed)) return 'Image URL must be a valid HTTP or HTTPS URL';
  return '';
};

/**
 * Attach image to create/update payload.
 * - create: include trimmed URL or null
 * - update: omit when unchanged (preserve); send null/URL when changed or cleared
 */
const applyImageToPayload = (payload, { current, original, isCreate }) => {
  const next = normalizeImageUrl(current);
  if (isCreate) {
    payload.image = next || null;
    return;
  }
  const prev = normalizeImageUrl(original);
  if (next === prev) return;
  payload.image = next || null;
};

/**
 * Optional session image: Main/Program–style file/URL toggle using shared uploadImage.
 * Mode switches do not clear the current URL. Upload failure keeps the previous value.
 */
const CategoryImageField = ({
  id,
  value,
  error,
  onChange,
  onClear,
  onUploadingChange,
  disabled = false,
  resetKey = 0,
}) => {
  const [imageSource, setImageSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);

  const trimmed = normalizeImageUrl(value);
  const canPreview = trimmed && HTTP_URL_RE.test(trimmed) && !error;

  useEffect(() => {
    setImageSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setImageSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || disabled) return;

    setUploading(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadImage(file);
      onChange(uploadedUrl);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          Image <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                imageSource === 'url'
                  ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                  : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {imageSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste an image URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={
              imageSource === 'url' ? 'Switch to file upload' : 'Switch to image URL'
            }
            disabled={uploading || disabled}
            onClick={() => switchSource(imageSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {imageSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(e) => {
                setUploadError('');
                onChange(e.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Optional HTTP/HTTPS URL. Clear removes a saved image.
          </p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept="image/*"
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg
                className="animate-spin h-4 w-4 text-brand"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading image...</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Optional image file. Uses the shared Admin upload helper.
          </p>
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {error && !uploadError && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {canPreview && !previewFailed && !uploading && (
        <div className="mt-2">
          <img
            src={trimmed}
            alt="Session preview"
            className="h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      )}
      {canPreview && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      )}
    </div>
  );
};

/**
 * Required session audio: Image-style file/URL toggle using shared uploadFile.
 * Mode switches do not clear the current URL. Upload failure keeps the previous value.
 * Clear is hidden while editing (API cannot clear audio after create).
 */
const SessionAudioField = ({
  id,
  value,
  error,
  onChange,
  onClear,
  onDurationChange,
  onUploadingChange,
  disabled = false,
  allowClear = true,
  resetKey = 0,
}) => {
  const [audioSource, setAudioSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const trimmed = String(value || '').trim();
  const canPreview = trimmed && HTTP_URL_RE.test(trimmed) && !error;

  useEffect(() => {
    setAudioSource('url');
    setUploading(false);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setAudioSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || disabled) return;

    if (file.type && !file.type.startsWith('audio/')) {
      setUploadError('File must be an audio file');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      const [uploadedUrl, fileDuration] = await Promise.all([
        uploadFile(file),
        readAudioFileDurationSeconds(file),
      ]);
      onChange(uploadedUrl);
      if (fileDuration != null) onDurationChange?.(fileDuration);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload audio. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled || !allowClear) return;
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = allowClear && (Boolean(trimmed) || Boolean(uploadError));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          Audio <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                audioSource === 'url'
                  ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                  : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {audioSource === 'url'
                ? 'Switch left to choose an audio file from your device'
                : 'Switch right to paste an audio URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={
              audioSource === 'url' ? 'Switch to file upload' : 'Switch to audio URL'
            }
            disabled={uploading || disabled}
            onClick={() => switchSource(audioSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              audioSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                audioSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {audioSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(e) => {
                setUploadError('');
                onChange(e.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Required HTTP/HTTPS URL
            {allowClear ? '.' : '. Cannot be cleared after create.'}
          </p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept="audio/*"
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg
                className="animate-spin h-4 w-4 text-brand"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading audio...</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Required audio file. Uses the shared Admin upload helper.
          </p>
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {error && !uploadError && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {canPreview && !uploading && (
        <div className="mt-2">
          <audio
            controls
            preload="metadata"
            className="w-full max-w-md h-9"
            src={trimmed}
            onLoadedMetadata={(e) => {
              const seconds = durationSecondsFromMedia(e.currentTarget.duration);
              if (seconds != null) onDurationChange?.(seconds);
            }}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  );
};

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="med-session-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="40" stroke="url(#med-session-gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" transform="rotate(-90 50 50)" />
      </svg>
    </div>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span
    className={`px-2 py-1 text-xs font-semibold rounded-lg ${
      isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'
    }`}
  >
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

/**
 * Build chip models from stored IDs + enrichment arrays (handles null slots / inactive).
 * Optional `catalog` (flat list with `_id`) fills gaps when enrichment is empty/null —
 * e.g. create form picks from the live catalog before any session enrichment exists.
 */
const buildAssignmentChips = (
  ids = [],
  enriched = [],
  groupLabelById = null,
  catalog = null
) => {
  const byId = new Map();
  (enriched || []).forEach((item) => {
    if (item && item._id) byId.set(String(item._id), item);
  });
  const catalogById = new Map();
  (catalog || []).forEach((item) => {
    if (item && item._id) catalogById.set(String(item._id), item);
  });
  return (ids || []).map((rawId, idx) => {
    const id = String(rawId);
    const fromMap = byId.get(id);
    const parallel = enriched?.[idx];
    const fromEnrichment = fromMap || (parallel && parallel._id ? parallel : null);
    const fromCatalog = catalogById.get(id) || null;
    // Prefer enrichment when present; fall back to catalog for picker/create flows
    const item = fromEnrichment || fromCatalog;
    const catalogGroup =
      groupLabelById && typeof groupLabelById.get === 'function'
        ? groupLabelById.get(id)
        : null;
    if (!item) {
      return {
        id,
        title: null,
        locked: true,
        kind: 'missing',
        label: 'Unavailable reference',
        groupLabel: catalogGroup || null,
        image: '',
      };
    }
    const enrichmentGroup =
      item.main_category?.title ||
      item.main_category_title ||
      null;
    const groupLabel = enrichmentGroup || catalogGroup || null;
    const image = item.image || fromCatalog?.image || '';
    if (item.is_active === false) {
      return {
        id,
        title: item.title || 'Untitled',
        locked: true,
        kind: 'inactive',
        label: item.title || 'Untitled',
        groupLabel,
        image,
      };
    }
    return {
      id,
      title: item.title || 'Untitled',
      locked: false,
      kind: 'active',
      label: item.title || 'Untitled',
      groupLabel,
      image,
    };
  });
};

const formatChipDisplay = (chip) => {
  if (chip.kind === 'missing') return 'Unavailable reference';
  const base = chip.label || 'Untitled';
  const withInactive = chip.kind === 'inactive' ? `${base} (inactive)` : base;
  if (chip.groupLabel) return `${chip.groupLabel} › ${withInactive}`;
  return withInactive;
};

const OptionThumb = ({ src, alt = '', size = 'sm' }) => {
  const [failed, setFailed] = useState(false);
  const trimmed = String(src || '').trim();
  const dim = size === 'md' ? 'h-6 w-6' : 'h-5 w-5';
  if (!trimmed || failed || !HTTP_URL_RE.test(trimmed)) return null;
  return (
    <img
      src={trimmed}
      alt={alt}
      className={`${dim} flex-shrink-0 rounded object-cover border border-gray-200 bg-gray-50`}
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Multi-assign picker: selected chips + searchable checkbox list.
 * Keeps locked (inactive/missing) chips visible; only active options are toggleable.
 */
const AssignmentFieldset = ({
  title,
  chips,
  options = [],
  groups = null,
  selectedIds,
  isDirty,
  isEditing,
  emptySelectedLabel,
  emptyOptionsLabel,
  searchPlaceholder,
  resetKey = 0,
  onToggle,
  onRemove,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery('');
  }, [resetKey]);

  const selectedCount = chips.length;
  const lockedCount = chips.filter((c) => c.locked).length;
  const useGroups = Array.isArray(groups);
  const totalOptionCount = useGroups
    ? groups.reduce((sum, g) => sum + (g.items?.length || 0), 0)
    : options.length;

  const filteredOptions = useMemo(() => {
    if (useGroups) return [];
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((item) =>
      String(item.title || '')
        .toLowerCase()
        .includes(q)
    );
  }, [useGroups, options, query]);

  const filteredGroups = useMemo(() => {
    if (!useGroups) return [];
    const q = query.trim().toLowerCase();
    return groups
      .map((group) => {
        const items = Array.isArray(group.items) ? group.items : [];
        if (!q) return { ...group, items };
        const mainHit = String(group.title || '')
          .toLowerCase()
          .includes(q);
        return {
          ...group,
          items: items.filter((item) => {
            if (mainHit) return true;
            return String(item.title || '')
              .toLowerCase()
              .includes(q);
          }),
        };
      })
      .filter((group) => group.items.length > 0);
  }, [useGroups, groups, query]);

  const renderOptionRow = (item) => {
    const id = String(item._id);
    const checked = selectedIds.includes(id);
    return (
      <label
        key={id}
        className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors ${
          checked ? 'bg-brand/5 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(id)}
          disabled={disabled}
          className="rounded border-gray-300 text-brand focus:ring-brand/30"
        />
        <OptionThumb src={item.image} alt="" />
        <span className="truncate flex-1 normal-case">{item.title}</span>
        {checked && (
          <span className="text-[10px] font-medium text-brand flex-shrink-0">Added</span>
        )}
      </label>
    );
  };

  return (
    <fieldset
      className="rounded-xl border border-gray-200 bg-gray-50/40 p-3 space-y-3"
      disabled={disabled}
    >
      <legend className="sr-only">{title}</legend>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-semibold text-gray-800 px-0.5" aria-hidden>
          {title}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-600">
            {selectedCount} selected
          </span>
          {isEditing && isDirty && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand/10 text-brand border border-brand/20">
              Edited
            </span>
          )}
          {isEditing && !isDirty && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-400">
              Unchanged
            </span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-gray-200 bg-white min-h-[2.75rem] p-2">
        {selectedCount === 0 ? (
          <p className="text-xs text-gray-400 px-1 py-1.5">{emptySelectedLabel}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => {
              const display = formatChipDisplay(chip);
              return (
                <span
                  key={`${title}-chip-${chip.id}`}
                  title={
                    chip.locked
                      ? chip.kind === 'missing'
                        ? 'Reference missing — kept until you edit this list'
                        : 'Inactive — kept until you edit this list'
                      : display
                  }
                  className={`inline-flex items-center gap-1 max-w-full pl-1.5 pr-1 py-1 rounded-lg text-xs border ${
                    chip.locked
                      ? 'bg-amber-50 text-amber-900 border-amber-200'
                      : 'bg-gray-100 text-gray-800 border-gray-200'
                  }`}
                >
                  <OptionThumb src={chip.image} alt="" />
                  {chip.locked && (
                    <HiLockClosed className="w-3 h-3 flex-shrink-0" aria-hidden />
                  )}
                  <span className="truncate">{display}</span>
                  {!chip.locked && (
                    <button
                      type="button"
                      onClick={() => onRemove(chip.id)}
                      disabled={disabled}
                      className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-50"
                      aria-label={`Remove ${display}`}
                    >
                      <HiX className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
        {lockedCount > 0 && (
          <p className="mt-2 text-[11px] text-amber-800/90 px-0.5">
            {lockedCount} locked item{lockedCount === 1 ? '' : 's'} stay until this list is
            edited and saved.
          </p>
        )}
      </div>

      <div className="relative">
        <HiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          disabled={disabled || totalOptionCount === 0}
          className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 disabled:opacity-50"
          aria-label={`Search ${title}`}
        />
      </div>

      <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white">
        {totalOptionCount === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">{emptyOptionsLabel}</p>
        ) : useGroups ? (
          filteredGroups.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-3">No matches for “{query.trim()}”</p>
          ) : (
            filteredGroups.map((group) => (
              <div key={group.id} className="border-b border-gray-100 last:border-b-0">
                <div className="sticky top-0 z-[1] px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <OptionThumb src={group.image} alt="" size="md" />
                  <p className="text-sm font-semibold text-gray-700 truncate normal-case">
                    {group.title}
                  </p>
                </div>
                <div className="divide-y divide-gray-50">
                  {group.items.map((item) => renderOptionRow(item))}
                </div>
              </div>
            ))
          )
        ) : filteredOptions.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">No matches for “{query.trim()}”</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredOptions.map((item) => renderOptionRow(item))}
          </div>
        )}
      </div>
    </fieldset>
  );
};

/** Optional text field → API: trimmed string or null when empty/whitespace. */
const toOptionalTextPayload = (value) => {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
};

const DEFAULT_MINIMUM_COMPLETION_PERCENTAGE = 70;

/** Parse optional positive integer from form text; empty → null; invalid → NaN. */
const parseOptionalPositiveInt = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return NaN;
  const n = Number(trimmed);
  return Number.isInteger(n) ? n : NaN;
};

const emptyForm = () => ({
  title: '',
  subtitle: '',
  highlight: '',
  description: '',
  image: '',
  audio_url: '',
  audio_duration_seconds: '',
  minimum_completion_percentage: String(DEFAULT_MINIMUM_COMPLETION_PERCENTAGE),
  session_type: '',
  is_active: true,
  sub_category_ids: [],
  program_category_ids: [],
});

const MeditationSessions = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterSubId, setFilterSubId] = useState('');
  const [filterProgramId, setFilterProgramId] = useState('');
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null); // null | 'sub' | 'program'
  const [currentPage, setCurrentPage] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listMutationLoading, setListMutationLoading] = useState(false);
  const [pageChangeLoading, setPageChangeLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reference catalogs (for filters + form pickers)
  const [mainWithSubs, setMainWithSubs] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [refsLoaded, setRefsLoaded] = useState(false);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [subEnrichment, setSubEnrichment] = useState([]);
  const [programEnrichment, setProgramEnrichment] = useState([]);
  const [subTouched, setSubTouched] = useState(false);
  const [programTouched, setProgramTouched] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [imageResetKey, setImageResetKey] = useState(0);

  const handleImageUploading = useCallback((busy) => {
    setImageUploading(Boolean(busy));
  }, []);
  const handleAudioUploading = useCallback((busy) => {
    setAudioUploading(Boolean(busy));
  }, []);
  const mediaUploading = imageUploading || audioUploading;

  // Confirm modals
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [itemToDeactivate, setItemToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [pendingDeactivatePayload, setPendingDeactivatePayload] = useState(null);
  const [showDropLockedConfirm, setShowDropLockedConfirm] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);
  const [lockedDropCount, setLockedDropCount] = useState(0);

  const searchTimerRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const isInitialDebounceRef = useRef(true);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);

  const getPaginationRange = () => {
    if (totalCount === 0) return [];
    const totalBlocks = 7;
    if (totalPages <= totalBlocks) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const left = Math.max(currentPage - 1, 1);
    const right = Math.min(currentPage + 1, totalPages);
    const showLeftDots = left > 2;
    const showRightDots = right < totalPages - 1;
    const pages = [1];
    if (showLeftDots) pages.push('dots');
    for (let p = showLeftDots ? left : 2; p <= (showRightDots ? right : totalPages - 1); p++) {
      pages.push(p);
    }
    if (showRightDots) pages.push('dots');
    pages.push(totalPages);
    return pages;
  };

  const loadReferenceCatalogs = useCallback(async () => {
    try {
      const [mainRes, progRes] = await Promise.all([
        listMeditationMainCategoriesWithSubcategories({}),
        listMeditationProgramCategories({}),
      ]);
      setMainWithSubs(Array.isArray(mainRes?.data) ? mainRes.data : []);
      setAllPrograms(Array.isArray(progRes?.data) ? progRes.data : []);
    } catch (err) {
      Toast.error(err.message || 'Failed to load category catalogs');
      setMainWithSubs([]);
      setAllPrograms([]);
    } finally {
      setRefsLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadReferenceCatalogs();
  }, [loadReferenceCatalogs]);

  useEffect(() => {
    if (!openFilterDropdown) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.session-filter-dropdown')) {
        setOpenFilterDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterDropdown]);

  const buildListBody = useCallback(
    (searchText, page, subId, programId) => {
      const body = {
        pagination: { page, pageSize: PAGE_SIZE },
      };
      if (searchText) body.searchtext = searchText;
      // Always list all statuses — no is_active filter
      if (subId) body.sub_category_id = subId;
      if (programId) body.program_category_id = programId;
      return body;
    },
    []
  );

  const loadSessions = useCallback(
    async (searchText, page, subId, programId) => {
      const reqId = ++listRequestIdRef.current;
      setError(null);
      try {
        const res = await listMeditationSessions(
          buildListBody(searchText, page, subId, programId)
        );
        if (reqId !== listRequestIdRef.current) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const total = typeof res?.totalCount === 'number' ? res.totalCount : rows.length;
        setSessions(rows);
        setTotalCount(total);
        if (rows.length === 0 && page > 1 && total > 0) {
          setCurrentPage(1);
        } else if (rows.length === 0 && page > 1 && total === 0) {
          setCurrentPage(1);
        }
      } catch (err) {
        if (reqId !== listRequestIdRef.current) return;
        setError(err.message || 'Error loading meditation sessions.');
        setSessions([]);
        setTotalCount(0);
      } finally {
        if (reqId === listRequestIdRef.current) {
          setIsLoading(false);
          setListMutationLoading(false);
          setPageChangeLoading(false);
        }
      }
    },
    [buildListBody]
  );

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (isInitialDebounceRef.current) {
      isInitialDebounceRef.current = false;
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    setIsLoading(true);
    loadSessions(debouncedSearch, currentPage, filterSubId, filterProgramId);
  }, [
    debouncedSearch,
    currentPage,
    filterSubId,
    filterProgramId,
    loadSessions,
  ]);

  const refreshList = async () => {
    setListMutationLoading(true);
    await loadSessions(
      debouncedSearch,
      currentPage,
      filterSubId,
      filterProgramId
    );
  };

  const changePage = (next) => {
    setCurrentPage((prev) => {
      const n = typeof next === 'function' ? next(prev) : next;
      if (n === prev) return prev;
      queueMicrotask(() => setPageChangeLoading(true));
      return n;
    });
  };

  /** Flat sub list for filters (includes inactive). */
  const allSubs = useMemo(() => {
    const rows = [];
    mainWithSubs.forEach((main) => {
      const mainTitle = main?.title || 'Untitled main';
      (Array.isArray(main?.sub_categories) ? main.sub_categories : []).forEach((sub) => {
        if (!sub || !sub._id) return;
        rows.push({
          ...sub,
          main_category_id: sub.main_category_id || main._id,
          main_category_title: mainTitle,
        });
      });
    });
    return rows;
  }, [mainWithSubs]);

  /** Active Main → active Sub groups for the Categories picker. */
  const categoryGroups = useMemo(
    () =>
      mainWithSubs
        .filter((main) => main && main.is_active !== false)
        .map((main) => ({
          id: String(main._id),
          title: main.title || 'Untitled main',
          image: main.image || '',
          items: (Array.isArray(main.sub_categories) ? main.sub_categories : []).filter(
            (sub) => sub && sub._id && sub.is_active !== false
          ),
        }))
        .filter((group) => group.items.length > 0),
    [mainWithSubs]
  );

  const subGroupLabelById = useMemo(() => {
    const map = new Map();
    allSubs.forEach((sub) => {
      if (sub?._id && sub.main_category_title) {
        map.set(String(sub._id), sub.main_category_title);
      }
    });
    return map;
  }, [allSubs]);

  const activeSubs = useMemo(
    () => allSubs.filter((s) => s.is_active !== false),
    [allSubs]
  );
  const activePrograms = useMemo(
    () => allPrograms.filter((p) => p.is_active !== false),
    [allPrograms]
  );

  const subChips = useMemo(
    () =>
      buildAssignmentChips(
        formData.sub_category_ids,
        subEnrichment,
        subGroupLabelById,
        allSubs
      ),
    [formData.sub_category_ids, subEnrichment, subGroupLabelById, allSubs]
  );
  const programChips = useMemo(
    () =>
      buildAssignmentChips(
        formData.program_category_ids,
        programEnrichment,
        null,
        allPrograms
      ),
    [formData.program_category_ids, programEnrichment, allPrograms]
  );

  const openAdd = () => {
    setFormData(emptyForm());
    setSubEnrichment([]);
    setProgramEnrichment([]);
    setSubTouched(true); // create always sends arrays
    setProgramTouched(true);
    setFormErrors({});
    setFormGeneralError('');
    setSelectedSession(null);
    setIsEditing(false);
    setPendingDeactivatePayload(null);
    setImageUploading(false);
    setAudioUploading(false);
    setImageResetKey((k) => k + 1);
    setShowForm(true);
  };

  const applySessionToForm = (doc) => {
    setSelectedSession(doc);
    const duration =
      doc.audio_duration_seconds != null && doc.audio_duration_seconds !== ''
        ? String(doc.audio_duration_seconds)
        : '';
    const pct =
      doc.minimum_completion_percentage != null &&
      doc.minimum_completion_percentage !== ''
        ? String(doc.minimum_completion_percentage)
        : String(DEFAULT_MINIMUM_COMPLETION_PERCENTAGE);
    setFormData({
      title: doc.title || '',
      subtitle: doc.subtitle || '',
      highlight: doc.highlight || '',
      description: doc.description || '',
      image: doc.image || '',
      audio_url: doc.audio_url || '',
      audio_duration_seconds: duration,
      minimum_completion_percentage: pct,
      session_type: sessionTypeToFormValue(doc.session_type),
      is_active: doc.is_active !== false,
      sub_category_ids: Array.isArray(doc.sub_category_ids)
        ? doc.sub_category_ids.map(String)
        : [],
      program_category_ids: Array.isArray(doc.program_category_ids)
        ? doc.program_category_ids.map(String)
        : [],
    });
    setSubEnrichment(Array.isArray(doc.sub_categories) ? doc.sub_categories : []);
    setProgramEnrichment(
      Array.isArray(doc.program_categories) ? doc.program_categories : []
    );
    setSubTouched(false);
    setProgramTouched(false);
  };

  const openEdit = async (item) => {
    setFormErrors({});
    setFormGeneralError('');
    setIsEditing(true);
    setImageUploading(false);
    setAudioUploading(false);
    setImageResetKey((k) => k + 1);
    applySessionToForm(item);
    setShowForm(true);
    setDetailLoading(true);
    try {
      const res = await getMeditationSession(item._id);
      const doc = res?.data || res;
      if (doc && doc._id) applySessionToForm(doc);
    } catch (err) {
      Toast.error(err.message || 'Failed to load session detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeForm = () => {
    if (saving || mediaUploading) return;
    setShowForm(false);
    setImageUploading(false);
    setAudioUploading(false);
    setFormData(emptyForm());
    setSubEnrichment([]);
    setProgramEnrichment([]);
    setSubTouched(false);
    setProgramTouched(false);
    setFormErrors({});
    setFormGeneralError('');
    setSelectedSession(null);
    setIsEditing(false);
    setPendingDeactivatePayload(null);
    setShowDropLockedConfirm(false);
    setPendingSavePayload(null);
  };

  const removeActiveSub = (id) => {
    setFormData((prev) => ({
      ...prev,
      sub_category_ids: prev.sub_category_ids.filter((x) => x !== id),
    }));
    setSubTouched(true);
  };

  const removeActiveProgram = (id) => {
    setFormData((prev) => ({
      ...prev,
      program_category_ids: prev.program_category_ids.filter((x) => x !== id),
    }));
    setProgramTouched(true);
  };

  const toggleActiveSub = (id) => {
    const sid = String(id);
    setFormData((prev) => {
      const has = prev.sub_category_ids.includes(sid);
      return {
        ...prev,
        sub_category_ids: has
          ? prev.sub_category_ids.filter((x) => x !== sid)
          : [...prev.sub_category_ids, sid],
      };
    });
    setSubTouched(true);
  };

  const toggleActiveProgram = (id) => {
    const pid = String(id);
    setFormData((prev) => {
      const has = prev.program_category_ids.includes(pid);
      return {
        ...prev,
        program_category_ids: has
          ? prev.program_category_ids.filter((x) => x !== pid)
          : [...prev.program_category_ids, pid],
      };
    });
    setProgramTouched(true);
  };

  const validateForm = () => {
    const errs = {};
    if (!String(formData.title || '').trim()) errs.title = 'Title is required';
    if (!String(formData.audio_url || '').trim()) {
      errs.audio_url = 'Audio URL is required';
    } else if (!HTTP_URL_RE.test(formData.audio_url.trim())) {
      errs.audio_url = 'Audio URL must be a valid HTTP or HTTPS URL';
    }
    const imageErr = getImageFieldError(formData.image);
    if (imageErr) errs.image = imageErr;

    const pctRaw = String(formData.minimum_completion_percentage ?? '').trim();
    if (!pctRaw) {
      errs.minimum_completion_percentage = 'Minimum completion % is required';
    } else {
      const pct = parseOptionalPositiveInt(pctRaw);
      if (!Number.isInteger(pct) || pct < 1 || pct > 100) {
        errs.minimum_completion_percentage = 'Minimum completion % must be a whole number from 1 to 100';
      }
    }

    const sessionTypeErr = validateSessionTypeFormValue(formData.session_type);
    if (sessionTypeErr) errs.session_type = sessionTypeErr;

    return errs;
  };

  const countLockedInSelection = (chips) => chips.filter((c) => c.locked).length;

  const resolveAudioDurationSeconds = async () => {
    const fromForm = parseOptionalPositiveInt(formData.audio_duration_seconds);
    if (Number.isInteger(fromForm) && fromForm >= 1) return fromForm;
    const url = String(formData.audio_url || '').trim();
    if (!HTTP_URL_RE.test(url)) return null;
    return readAudioDurationSeconds(url);
  };

  const buildUpdatePayload = (dropLocked, durationSeconds) => {
    const payload = {
      session_id: selectedSession._id,
      title: formData.title.trim(),
      subtitle: toOptionalTextPayload(formData.subtitle),
      highlight: toOptionalTextPayload(formData.highlight),
      description: toOptionalTextPayload(formData.description),
      is_active: formData.is_active,
    };
    applyImageToPayload(payload, {
      current: formData.image,
      original: selectedSession?.image,
      isCreate: false,
    });
    const audioTrim = String(formData.audio_url || '').trim();
    if (audioTrim) payload.audio_url = audioTrim;

    applyAudioDurationToPayload(payload, {
      current: durationSeconds,
      original: selectedSession?.audio_duration_seconds,
      isCreate: false,
    });

    const pctParsed = parseOptionalPositiveInt(formData.minimum_completion_percentage);
    if (Number.isInteger(pctParsed)) {
      payload.minimum_completion_percentage = pctParsed;
    }

    if (subTouched) {
      if (dropLocked) {
        payload.sub_category_ids = formData.sub_category_ids.filter((id) =>
          subChips.some((c) => c.id === id && !c.locked)
        );
      } else {
        payload.sub_category_ids = [...formData.sub_category_ids];
      }
    }
    if (programTouched) {
      if (dropLocked) {
        payload.program_category_ids = formData.program_category_ids.filter((id) =>
          programChips.some((c) => c.id === id && !c.locked)
        );
      } else {
        payload.program_category_ids = [...formData.program_category_ids];
      }
    }
    return applySessionTypeToUpdatePayload(
      payload,
      formData.session_type,
      selectedSession?.session_type
    );
  };

  const buildCreatePayload = (durationSeconds) => {
    const payload = {
      title: formData.title.trim(),
      subtitle: toOptionalTextPayload(formData.subtitle),
      highlight: toOptionalTextPayload(formData.highlight),
      description: toOptionalTextPayload(formData.description),
      audio_url: formData.audio_url.trim(),
      is_active: formData.is_active,
      sub_category_ids: formData.sub_category_ids.filter((id) =>
        activeSubs.some((s) => String(s._id) === id)
      ),
      program_category_ids: formData.program_category_ids.filter((id) =>
        activePrograms.some((p) => String(p._id) === id)
      ),
    };
    applyImageToPayload(payload, {
      current: formData.image,
      original: null,
      isCreate: true,
    });
    applyAudioDurationToPayload(payload, {
      current: durationSeconds,
      original: null,
      isCreate: true,
    });
    const pctParsed = parseOptionalPositiveInt(formData.minimum_completion_percentage);
    if (Number.isInteger(pctParsed)) {
      payload.minimum_completion_percentage = pctParsed;
    } else {
      payload.minimum_completion_percentage = DEFAULT_MINIMUM_COMPLETION_PERCENTAGE;
    }
    return applySessionTypeToCreatePayload(payload, formData.session_type);
  };

  const executeSave = async (payload, mode) => {
    setSaving(true);
    setFormErrors({});
    setFormGeneralError('');
    try {
      if (mode === 'create') {
        await createMeditationSession(payload);
        Toast.success('Session created successfully');
      } else {
        await updateMeditationSession(payload);
        Toast.success('Session updated successfully');
      }
      closeForm();
      if (mode === 'create') {
        setCurrentPage(1);
        setListMutationLoading(true);
        await loadSessions(
          debouncedSearch,
          1,
          filterSubId,
          filterProgramId
        );
      } else {
        await refreshList();
      }
    } catch (err) {
      const message = err.message || 'Failed to save session';
      setFormGeneralError(message);
      if (/session_type/i.test(message)) {
        setFormErrors((prev) => ({
          ...prev,
          session_type: message,
        }));
      }
    } finally {
      setSaving(false);
      setShowDropLockedConfirm(false);
      setPendingSavePayload(null);
      setLockedDropCount(0);
    }
  };

  const handleSave = async () => {
    if (mediaUploading) return;
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    setSaving(true);
    let durationSeconds = null;
    try {
      durationSeconds = await resolveAudioDurationSeconds();
    } catch {
      durationSeconds = null;
    }
    if (Number.isInteger(durationSeconds) && durationSeconds >= 1) {
      setFormData((prev) => ({
        ...prev,
        audio_duration_seconds: String(durationSeconds),
      }));
    }

    if (isEditing && selectedSession) {
      const wasActive = selectedSession.is_active !== false;
      const willDeactivate = wasActive && formData.is_active === false;

      const lockedCount =
        (subTouched ? countLockedInSelection(subChips) : 0) +
        (programTouched ? countLockedInSelection(programChips) : 0);

      if (willDeactivate) {
        // Soft-deactivate via update; if assignment arrays were edited and locked
        // refs exist, drop locked IDs (API rejects inactive) — warn in confirm modal.
        setSaving(false);
        setPendingDeactivatePayload(buildUpdatePayload(lockedCount > 0, durationSeconds));
        setLockedDropCount(lockedCount);
        setItemToDeactivate(selectedSession);
        setShowDeactivateModal(true);
        return;
      }

      if (lockedCount > 0 && (subTouched || programTouched)) {
        setSaving(false);
        setPendingSavePayload(buildUpdatePayload(true, durationSeconds));
        setLockedDropCount(lockedCount);
        setShowDropLockedConfirm(true);
        return;
      }

      await executeSave(buildUpdatePayload(false, durationSeconds), 'update');
      return;
    }

    await executeSave(buildCreatePayload(durationSeconds), 'create');
  };

  const openSoftDelete = (item) => {
    setPendingDeactivatePayload(null);
    setItemToDeactivate(item);
    setShowDeactivateModal(true);
  };

  const closeDeactivate = () => {
    if (deactivating) return;
    setShowDeactivateModal(false);
    setItemToDeactivate(null);
    setPendingDeactivatePayload(null);
    setLockedDropCount(0);
  };

  const confirmDeactivate = async () => {
    if (!itemToDeactivate) return;
    setDeactivating(true);
    try {
      if (pendingDeactivatePayload) {
        await updateMeditationSession(pendingDeactivatePayload);
      } else {
        await deleteMeditationSession(itemToDeactivate._id);
      }
      setShowDeactivateModal(false);
      setItemToDeactivate(null);
      setPendingDeactivatePayload(null);
      setShowForm(false);
      setFormData(emptyForm());
      setSelectedSession(null);
      setIsEditing(false);
      await refreshList();
      Toast.success('Session deactivated successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to deactivate session');
    } finally {
      setDeactivating(false);
    }
  };

  const confirmDropLockedAndSave = async () => {
    if (!pendingSavePayload) return;
    await executeSave(pendingSavePayload, 'update');
  };

  const showLoading = isLoading || listMutationLoading || pageChangeLoading;
  const hasFilters =
    Boolean(debouncedSearch) ||
    Boolean(filterSubId) ||
    Boolean(filterProgramId);

  const renderListChips = (session, type) => {
    const ids =
      type === 'sub'
        ? session.sub_category_ids || []
        : session.program_category_ids || [];
    const enriched =
      type === 'sub' ? session.sub_categories || [] : session.program_categories || [];
    const chips = buildAssignmentChips(
      ids,
      enriched,
      type === 'sub' ? subGroupLabelById : null,
      type === 'sub' ? allSubs : allPrograms
    );
    if (!chips.length) return <span className="text-xs text-gray-400">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {chips.slice(0, 4).map((chip) => (
          <span
            key={`${type}-${chip.id}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs ${
              chip.locked
                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                : 'bg-gray-100 text-gray-700'
            }`}
            title={chip.locked ? `${chip.label} (${chip.kind})` : chip.label}
          >
            {chip.locked && <HiLockClosed className="w-3 h-3" aria-hidden />}
            <span className="max-w-[7rem] truncate">
              {chip.kind === 'missing' ? 'Unavailable' : chip.label}
              {chip.kind === 'inactive' ? ' (inactive)' : ''}
            </span>
          </span>
        ))}
        {chips.length > 4 && (
          <span className="text-xs text-gray-400">+{chips.length - 4}</span>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
                aria-label="Open menu"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">Meditation</h1>
                <p className="text-sm text-gray-500 mt-0.5">Sessions</p>
              </div>
            </div>
          </div>

          <MeditationNav />

          <div className="mb-4 md:mb-6 flex-shrink-0">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto md:flex-1 min-w-0">
                <div className="w-full sm:w-56 relative flex-shrink-0">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search title, subtitle, highlight…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-10 pl-9 pr-3 text-sm leading-normal border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
                    aria-label="Search sessions"
                  />
                </div>
                <div className="w-full sm:w-52 flex-shrink-0 relative session-filter-dropdown">
                  <button
                    type="button"
                    id="session-sub-filter"
                    disabled={!refsLoaded}
                    onClick={() =>
                      setOpenFilterDropdown((prev) => (prev === 'sub' ? null : 'sub'))
                    }
                    className="w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border border-[#ECECEC] rounded-xl focus:outline-none bg-white disabled:opacity-60 truncate"
                    aria-haspopup="listbox"
                    aria-expanded={openFilterDropdown === 'sub'}
                    aria-label="Category filter"
                  >
                    {(() => {
                      if (!filterSubId) return 'All categories';
                      const sub = allSubs.find((s) => String(s._id) === String(filterSubId));
                      if (!sub) return 'All categories';
                      const label = sub.main_category_title
                        ? `${sub.main_category_title} › ${sub.title}`
                        : sub.title;
                      return sub.is_active === false ? `${label} (inactive)` : label;
                    })()}
                  </button>
                  <HiChevronDown
                    className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
                      openFilterDropdown === 'sub' ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                  {openFilterDropdown === 'sub' && (
                    <div
                      className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200"
                      role="listbox"
                      aria-labelledby="session-sub-filter"
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={!filterSubId}
                        onClick={() => {
                          setFilterSubId('');
                          setCurrentPage(1);
                          setOpenFilterDropdown(null);
                        }}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 ${
                          !filterSubId ? 'bg-gray-50 font-semibold' : ''
                        }`}
                      >
                        All categories
                      </button>
                      {mainWithSubs.map((main) => {
                        const subs = Array.isArray(main?.sub_categories)
                          ? main.sub_categories.filter((s) => s && s._id)
                          : [];
                        if (!subs.length) return null;
                        return (
                          <div key={main._id}>
                            <div className="px-3 py-2 text-sm font-semibold text-gray-600 bg-gray-50/80 sticky top-0 flex items-center gap-2">
                              <OptionThumb src={main.image} alt="" />
                              <span className="truncate normal-case">
                                {main.title || 'Untitled main'}
                                {main.is_active === false ? ' (inactive)' : ''}
                              </span>
                            </div>
                            {subs.map((s) => {
                              const selected = String(s._id) === String(filterSubId);
                              return (
                                <button
                                  key={s._id}
                                  type="button"
                                  role="option"
                                  aria-selected={selected}
                                  onClick={() => {
                                    setFilterSubId(String(s._id));
                                    setCurrentPage(1);
                                    setOpenFilterDropdown(null);
                                  }}
                                  className={`w-full pl-3 pr-3 py-2 text-sm text-left hover:bg-gray-100 flex items-center gap-2 ${
                                    selected ? 'bg-gray-50 font-semibold' : ''
                                  }`}
                                >
                                  <OptionThumb src={s.image} alt="" />
                                  <span className="truncate normal-case">
                                    {s.title}
                                    {s.is_active === false ? ' (inactive)' : ''}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="w-full sm:w-52 flex-shrink-0 relative session-filter-dropdown">
                  <button
                    type="button"
                    id="session-program-filter"
                    disabled={!refsLoaded}
                    onClick={() =>
                      setOpenFilterDropdown((prev) =>
                        prev === 'program' ? null : 'program'
                      )
                    }
                    className="w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border border-[#ECECEC] rounded-xl focus:outline-none bg-white disabled:opacity-60 truncate"
                    aria-haspopup="listbox"
                    aria-expanded={openFilterDropdown === 'program'}
                    aria-label="Program category filter"
                  >
                    {filterProgramId
                      ? allPrograms.find((p) => String(p._id) === String(filterProgramId))
                          ?.title || 'All program categories'
                      : 'All program categories'}
                  </button>
                  <HiChevronDown
                    className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
                      openFilterDropdown === 'program' ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                  {openFilterDropdown === 'program' && (
                    <div
                      className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200"
                      role="listbox"
                      aria-labelledby="session-program-filter"
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={!filterProgramId}
                        onClick={() => {
                          setFilterProgramId('');
                          setCurrentPage(1);
                          setOpenFilterDropdown(null);
                        }}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 ${
                          !filterProgramId ? 'bg-gray-50 font-semibold' : ''
                        }`}
                      >
                        All program categories
                      </button>
                      {allPrograms.map((p) => {
                        const selected = String(p._id) === String(filterProgramId);
                        return (
                          <button
                            key={p._id}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              setFilterProgramId(String(p._id));
                              setCurrentPage(1);
                              setOpenFilterDropdown(null);
                            }}
                            className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                              selected ? 'bg-gray-50 font-semibold' : ''
                            }`}
                          >
                            {p.title}
                            {p.is_active === false ? ' (inactive)' : ''}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {(totalCount > 0 || hasFilters || Boolean(error)) && (
                <button
                  type="button"
                  onClick={openAdd}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap self-start md:self-auto flex-shrink-0"
                >
                  <HiPlus className="w-5 h-5" />
                  Add Session
                </button>
              )}
            </div>
          </div>

          {showLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="flex-1 min-h-0 flex items-center justify-center px-4">
              <div className="text-center text-gray-500 max-w-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {hasFilters ? 'No matching sessions' : 'No sessions yet'}
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {hasFilters
                    ? 'Try adjusting search or filters'
                    : 'Create a session with an audio URL and optional category assignments'}
                </p>
                {!hasFilters && (
                  <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-brand hover:opacity-90"
                  >
                    <HiPlus className="w-4 h-4" /> Add Session
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center">
                <span className="text-xs text-gray-500">
                  {totalCount > 0
                    ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                        currentPage * PAGE_SIZE,
                        totalCount
                      )} of ${totalCount}`
                    : '0 sessions'}
                </span>
              </div>

              {error ? (
                <div className="px-4 py-6 text-center">
                  <p className="text-red-500 mb-3 whitespace-pre-line">{error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoading(true);
                      loadSessions(
                        debouncedSearch,
                        currentPage,
                        filterSubId,
                        filterProgramId
                      );
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Subcategories
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Programs
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell w-32">
                          Type
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                          Status
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sessions.map((session) => (
                        <tr key={session._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-800">{session.title}</div>
                            {session.subtitle ? (
                              <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                                {session.subtitle}
                              </p>
                            ) : null}
                            {session.highlight ? (
                              <span className="inline-block mt-1 max-w-full truncate px-2 py-0.5 text-[11px] font-medium rounded-lg bg-gray-100 text-gray-700 border border-gray-200">
                                {session.highlight}
                              </span>
                            ) : null}
                            {!session.subtitle && session.description ? (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                {session.description}
                              </p>
                            ) : null}
                            <span className="md:hidden inline-block mt-1 px-2 py-0.5 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-600">
                              {formatSessionTypeDisplay(session.session_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {renderListChips(session, 'sub')}
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {renderListChips(session, 'program')}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-medium border ${
                                session.session_type
                                  ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                                  : 'bg-gray-50 text-gray-500 border-gray-200'
                              }`}
                              title={
                                session.session_type
                                  ? String(session.session_type)
                                  : 'Unclassified (null)'
                              }
                            >
                              {formatSessionTypeDisplay(session.session_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge isActive={session.is_active !== false} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(session)}
                                className="p-1.5 rounded-lg hover:opacity-90"
                                style={{ backgroundColor: '#F2E2F7' }}
                                title="Edit"
                                aria-label="Edit session"
                              >
                                <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                              <button
                                type="button"
                                onClick={() => openSoftDelete(session)}
                                disabled={session.is_active === false}
                                className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                                title={
                                  session.is_active === false
                                    ? 'Already inactive'
                                    : 'Deactivate session'
                                }
                                aria-label="Deactivate session"
                              >
                                <MdDelete className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalCount > 0 && (
                <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => changePage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Previous page"
                    >
                      <HiChevronLeft className="w-4 h-4" />
                    </button>
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={page}
                          onClick={() => changePage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-brand text-white shadow-sm hover:opacity-90'
                              : 'text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => changePage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage >= totalPages}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      <HiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="session-form-title"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 id="session-form-title" className="text-lg font-bold text-gray-800">
                {isEditing ? 'Edit Session' : 'Add Session'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {detailLoading && (
                <p className="text-sm text-gray-500" role="status">
                  Loading latest details…
                </p>
              )}
              {formGeneralError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
                  {formGeneralError}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (formErrors.title) setFormErrors({ ...formErrors, title: '' });
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    formErrors.title ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
                  }`}
                />
                {formErrors.title && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Subtitle <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) =>
                    setFormData({ ...formData, subtitle: e.target.value })
                  }
                  placeholder="Short supporting line under the title"
                  disabled={saving || detailLoading}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Highlight <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.highlight}
                  onChange={(e) =>
                    setFormData({ ...formData, highlight: e.target.value })
                  }
                  placeholder="Short badge/chip text (duration, tone, etc.)"
                  disabled={saving || detailLoading}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none focus:border-gray-500"
                />
              </div>

              <CategoryImageField
                id="session-image"
                resetKey={imageResetKey}
                value={formData.image}
                error={formErrors.image}
                disabled={saving || detailLoading}
                onUploadingChange={handleImageUploading}
                onChange={(next) => {
                  setFormData((prev) => ({ ...prev, image: next }));
                  if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                }}
                onClear={() => {
                  setFormData((prev) => ({ ...prev, image: '' }));
                  if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                }}
              />

              <SessionAudioField
                id="session-audio"
                resetKey={imageResetKey}
                value={formData.audio_url}
                error={formErrors.audio_url}
                disabled={saving || detailLoading}
                allowClear={!isEditing}
                onUploadingChange={handleAudioUploading}
                onChange={(next) => {
                  setFormData((prev) => {
                    const same =
                      String(prev.audio_url || '').trim() === String(next || '').trim();
                    return {
                      ...prev,
                      audio_url: next,
                      audio_duration_seconds: same ? prev.audio_duration_seconds : '',
                    };
                  });
                  if (formErrors.audio_url) {
                    setFormErrors((prev) => ({ ...prev, audio_url: '' }));
                  }
                }}
                onDurationChange={(seconds) => {
                  if (!Number.isInteger(seconds) || seconds < 1) return;
                  setFormData((prev) => ({
                    ...prev,
                    audio_duration_seconds: String(seconds),
                  }));
                }}
                onClear={() => {
                  setFormData((prev) => ({
                    ...prev,
                    audio_url: '',
                    audio_duration_seconds: '',
                  }));
                  if (formErrors.audio_url) {
                    setFormErrors((prev) => ({ ...prev, audio_url: '' }));
                  }
                }}
              />

              <div>
                <label
                  htmlFor="meditation-session-type"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Session type{' '}
                  <span className="font-normal text-gray-400">(exploration badges)</span>
                </label>
                <select
                  id="meditation-session-type"
                  value={formData.session_type}
                  onChange={(e) => {
                    setFormData({ ...formData, session_type: e.target.value });
                    if (formErrors.session_type) {
                      setFormErrors({ ...formErrors, session_type: '' });
                    }
                  }}
                  disabled={saving || detailLoading}
                  className={`w-full h-10 px-3 text-sm border rounded-lg focus:outline-none bg-white disabled:opacity-50 ${
                    formErrors.session_type
                      ? 'border-red-300'
                      : 'border-gray-300 focus:border-gray-500'
                  }`}
                >
                  {SESSION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'unclassified'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {formErrors.session_type ? (
                  <p className="mt-1 text-xs text-red-600 whitespace-pre-line">
                    {formErrors.session_type}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 flex items-start gap-1">
                    <HiInformationCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{SESSION_TYPE_HELPER_TEXT}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Minimum completion %{' '}
                  <span className="font-normal text-gray-400">(1–100, default 70)</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.minimum_completion_percentage}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      minimum_completion_percentage: e.target.value,
                    });
                    if (formErrors.minimum_completion_percentage) {
                      setFormErrors({
                        ...formErrors,
                        minimum_completion_percentage: '',
                      });
                    }
                  }}
                  placeholder="70"
                  disabled={saving || detailLoading}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                    formErrors.minimum_completion_percentage
                      ? 'border-red-300'
                      : 'border-gray-300 focus:border-gray-500'
                  }`}
                />
                {formErrors.minimum_completion_percentage && (
                  <p className="mt-1 text-xs text-red-600">
                    {formErrors.minimum_completion_percentage}
                  </p>
                )}
              </div>

              <AssignmentFieldset
                title="Categories"
                chips={subChips}
                groups={categoryGroups}
                selectedIds={formData.sub_category_ids}
                isDirty={subTouched}
                isEditing={isEditing}
                emptySelectedLabel="No categories selected yet — pick a subcategory under a main below."
                emptyOptionsLabel="No active main categories with subcategories available."
                searchPlaceholder="Search main or subcategory…"
                resetKey={imageResetKey}
                disabled={saving || detailLoading}
                onToggle={toggleActiveSub}
                onRemove={removeActiveSub}
              />

              <AssignmentFieldset
                title="Program Categories"
                chips={programChips}
                options={activePrograms}
                selectedIds={formData.program_category_ids}
                isDirty={programTouched}
                isEditing={isEditing}
                emptySelectedLabel="No program categories selected yet — pick from the list below."
                emptyOptionsLabel="No active program categories available."
                searchPlaceholder="Search program categories…"
                resetKey={imageResetKey}
                disabled={saving || detailLoading}
                onToggle={toggleActiveProgram}
                onRemove={removeActiveProgram}
              />

              {isEditing && (subTouched || programTouched) && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  You changed assignments. On save, inactive or missing items cannot be kept and
                  you may be asked to confirm dropping them.
                </p>
              )}
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_active}
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  disabled={saving || detailLoading || mediaUploading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    formData.is_active ? 'bg-brand' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving || mediaUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || detailLoading || mediaUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Spinner />
                      {isEditing ? 'Updating...' : 'Saving...'}
                    </>
                  ) : mediaUploading ? (
                    <>
                      <Spinner />
                      Uploading...
                    </>
                  ) : isEditing ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Soft-deactivate confirm */}
      {showDeactivateModal && itemToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-6">
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            role="dialog"
            aria-modal="true"
            aria-labelledby="session-deactivate-title"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 id="session-deactivate-title" className="text-xl font-bold text-gray-800">
                Deactivate Session
              </h2>
            </div>
            <div className="px-6 py-4 space-y-2">
              <p className="text-gray-700">
                Deactivate <strong>{itemToDeactivate.title}</strong>?
              </p>
              {pendingDeactivatePayload ? (
                <p className="text-sm text-gray-600">
                  Soft-deactivation via update. Category ID arrays remain on the document unless
                  you also edited assignments.
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  Soft-deactivation keeps category ID arrays on the document.
                </p>
              )}
              {pendingDeactivatePayload && lockedDropCount > 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  You also edited assignments with {lockedDropCount} locked inactive/unavailable
                  reference{lockedDropCount === 1 ? '' : 's'}. Those locked IDs will be dropped
                  from the arrays because the API only accepts active IDs when arrays are sent.
                </p>
              )}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {deactivating ? (
                  <>
                    <Spinner />
                    Deactivating...
                  </>
                ) : (
                  'Confirm Deactivate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dropping locked refs when sending assignment arrays */}
      {showDropLockedConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-6">
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            role="dialog"
            aria-modal="true"
            aria-labelledby="drop-locked-title"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 id="drop-locked-title" className="text-xl font-bold text-gray-800">
                Update Assignments?
              </h2>
            </div>
            <div className="px-6 py-4 space-y-2">
              <p className="text-gray-700">
                You edited category assignments while this session still has{' '}
                <strong>{lockedDropCount}</strong> inactive or unavailable locked reference
                {lockedDropCount === 1 ? '' : 's'}.
              </p>
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                The API only accepts active category IDs when arrays are sent. Continuing will
                remove those locked references from the session. Cancel to keep them (leave
                assignment fields untouched and save other fields only).
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDropLockedConfirm(false);
                  setPendingSavePayload(null);
                  setLockedDropCount(0);
                }}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDropLockedAndSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Spinner />
                    Saving...
                  </>
                ) : (
                  'Continue and Drop Locked'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeditationSessions;
